import gsap from "gsap";
import { module as BaseModule } from "modujs";
import Swiper from "swiper";
import { EffectFade, Keyboard, Mousewheel, Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/keyboard";
import "swiper/css/navigation";

const DESKTOP_MQ = "(min-width: 768px) and (pointer: fine)";
const HOME_SLIDE_STORAGE_KEY = "julietmerie:home-slider-index";

const mousewheel = {
  // Enabled after init on desktop only via matchMedia.
  enabled: false,
  // Allow vertical wheel to drive horizontal / fade sliders.
  forceToAxis: false,
  releaseOnEdges: true,
  // One slide per gesture — ignore rapid successive wheel events.
  thresholdTime: 500,
  thresholdDelta: 20,
};

const readStoredHomeIndex = () => {
  try {
    const raw = sessionStorage.getItem(HOME_SLIDE_STORAGE_KEY);
    if (raw == null) return null;
    const index = Number.parseInt(raw, 10);
    return Number.isFinite(index) && index >= 0 ? index : null;
  } catch {
    return null;
  }
};

const storeHomeIndex = (index) => {
  try {
    sessionStorage.setItem(HOME_SLIDE_STORAGE_KEY, String(index));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

export default class extends BaseModule {
  init() {
    const slider = this.el
    const { sliderConfig } = slider.dataset
    // Allow a rail wrapper (nav outside) — Swiper mounts on .swiper if present.
    const swiperRoot = slider.classList.contains("swiper")
      ? slider
      : slider.querySelector(".swiper") || slider

    this.sliderConfig = sliderConfig
    const storedHomeIndex = sliderConfig === "default" ? readStoredHomeIndex() : null

    const params = {
      default: {
        modules: [Navigation, Keyboard, Mousewheel],
        navigation: {
          nextEl: '[data-module-slider-next]',
          prevEl: '[data-module-slider-prev]',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        mousewheel,
        centeredSlides: true,
        slidesPerView: "auto",
        spaceBetween: 20,
        loop: true,
        initialSlide: storedHomeIndex ?? 0,
        breakpoints: {
          768: {
            centeredSlides: false,
          },
        },
        on: {
          slideChange(swiper) {
            storeHomeIndex(swiper.realIndex)
          },
        },
      },
      project: {
        modules: [Navigation, Keyboard, Mousewheel, Pagination, EffectFade],
        navigation: {
          nextEl: '[data-module-slider-next]',
          prevEl: '[data-module-slider-prev]',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        pagination: {
          el: '.swiper-pagination',
          type: "fraction",
        },
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        mousewheel,
        // Mobile: grow with slide content so tall images can scroll.
        // Desktop (lg): fixed viewport height via CSS.
        autoHeight: true,
        slidesPerView: 1,
        loop: true,
        breakpoints: {
          1024: {
            autoHeight: false,
          },
        },
        on: {
          click(swiper, event) {
            // Keep dedicated controls handling their own interactions.
            if (event.target.closest('[data-module-slider-next], [data-module-slider-prev], .swiper-pagination')) {
              return;
            }
            // Let video interactions (play/pause/controls) stay local.
            if (event.target.closest('video')) {
              return;
            }
            swiper.slideNext();
          },
          slideChangeTransitionStart() {
            if (window.matchMedia("(min-width: 1024px)").matches) return;
            window.lenis?.scrollTo(0, { immediate: true });
          },
          slideChangeTransitionEnd() {
            window.lenis?.resize();
          },
          resize() {
            window.lenis?.resize();
          },
        },
      },
      related: {
        modules: [Navigation, Keyboard],
        navigation: {
          nextEl: slider.querySelector("[data-module-slider-next]"),
          prevEl: slider.querySelector("[data-module-slider-prev]"),
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        slidesPerView: 2,
        spaceBetween: 8,
        watchOverflow: true,
        breakpoints: {
          768: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 12,
          },
        },
      },
    }

    this.swiper = new Swiper(swiperRoot, params[sliderConfig])
    this.desktopMq = window.matchMedia(DESKTOP_MQ)
    this.onDesktopMqChange = () => this.syncMousewheel()
    this.syncMousewheel()
    this.desktopMq.addEventListener("change", this.onDesktopMqChange)

    // Loop mode can ignore initialSlide on first paint — snap after init.
    if (sliderConfig === "default" && storedHomeIndex != null && this.swiper) {
      this.swiper.slideToLoop(storedHomeIndex, 0)
    }

    if (sliderConfig === "project") {
      this.bindProjectAutoHeight()
    }

    gsap.to(slider, {
      opacity: 1,
      duration: 3,
      ease: "power2.Out",
    })
  }

  bindProjectAutoHeight() {
    const updateHeight = () => {
      if (!this.swiper || window.matchMedia("(min-width: 1024px)").matches) return
      this.swiper.updateAutoHeight(0)
      window.lenis?.resize()
    }

    this.el.querySelectorAll("img").forEach((img) => {
      if (img.complete) return
      img.addEventListener("load", updateHeight, { once: true })
    })

    // Catch late layout after fonts / images settle.
    requestAnimationFrame(updateHeight)
  }

  syncMousewheel() {
    if (!this.swiper?.mousewheel) return

    if (this.desktopMq.matches) {
      this.swiper.mousewheel.enable()
    } else {
      this.swiper.mousewheel.disable()
    }
  }

  destroy() {
    if (this.sliderConfig === "default" && this.swiper) {
      storeHomeIndex(this.swiper.realIndex)
    }
    this.desktopMq?.removeEventListener("change", this.onDesktopMqChange)
    this.swiper?.destroy(true, true)
  }
}
